import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Crown, CheckCircle2, Building2, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

// Bare axios instance — no interceptors, no auth headers leaked to a public endpoint.
const publicClient = axios.create({ withCredentials: false });

/**
 * Reverse-index display: shows which OnlyBest participants have pinned this
 * business as one of their Best-of-Best picks. Drives the social-proof
 * flywheel ("more creators recommend → more trust → more business").
 *
 * Renders nothing if no endorsers exist (so unclaimed listings stay clean).
 */
export default function EndorsedBy({ slug }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    publicClient
      .get(`${API}/onlybest/business/${slug}/endorsed-by`, {
        headers: { Authorization: "" }, // explicitly strip any token
      })
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch(() => {
        if (!cancelled) setData({ count: 0, endorsers: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (!data || data.count === 0) return null;

  const visible = expanded ? data.endorsers : data.endorsers.slice(0, 4);

  return (
    <section className="mt-10 rounded-2xl border border-[#FBBF24]/30 bg-gradient-to-br from-[#FBBF24]/10 via-[#0F172A] to-[#EC4899]/10 p-5 sm:p-6" data-testid="endorsed-by-section">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2 min-w-0">
          <Crown className="w-4 h-4 text-[#FBBF24] flex-shrink-0" />
          <h3 className="text-base sm:text-lg font-semibold text-white truncate">
            Endorsed by <span className="text-[#FBBF24]">{data.count}</span> OnlyBest {data.count === 1 ? "participant" : "participants"}
          </h3>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Real recommendations · No paid placements</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="endorsers-grid">
        {visible.map((e) => (
          <Link
            key={e.slug}
            to={`/business/${e.slug}`}
            className="flex items-start gap-3 p-3 rounded-xl border border-white/10 bg-[#0F172A] hover:border-[#FBBF24]/40 transition min-w-0"
            data-testid={`endorser-${e.slug}`}
          >
            {e.logo_url ? (
              <img src={e.logo_url} alt={e.name} className="w-11 h-11 rounded-lg object-cover bg-white/10 flex-shrink-0" />
            ) : (
              <div className="w-11 h-11 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-5 h-5 text-[#94A3B8]" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-semibold truncate flex items-center gap-1.5">
                {e.name}
                {e.is_verified && <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981]" />}
              </p>
              <p className="text-[11px] text-[#94A3B8] truncate">
                {e.kind === "creator" ? "👑 Creator" : e.kind === "agent" ? "🤝 Agent" : "🏢 Business"}
                {(e.city || e.state) ? ` · ${[e.city, e.state].filter(Boolean).join(", ")}` : ""}
              </p>
              {e.note && <p className="text-xs text-[#CBD5E1] italic mt-1.5 line-clamp-2">"{e.note}"</p>}
            </div>
          </Link>
        ))}
      </div>

      {data.endorsers.length > 4 && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-4 mx-auto flex items-center gap-1.5 text-xs text-[#FBBF24] hover:text-white"
          data-testid="endorsers-toggle"
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
          {expanded ? "Show less" : `Show all ${data.endorsers.length}`}
        </button>
      )}
    </section>
  );
}
